<template>
  <div :class="['themed-grid', customClass]">
    <!-- Grid Container -->
    <div :class="['themed-grid__container', gridClass]">
      <div
        v-for="(item, index) in paginatedData"
        :key="getItemKey(item, index)"
        class="themed-grid__card"
        @click="handleCardClick(item)"
        role="button"
        tabindex="0"
        @keypress.enter="handleCardClick(item)"
      >
        <!-- Custom slot for complex layouts that can't be configured -->
        <slot v-if="$scopedSlots.default" :item="item" :index="index"></slot>

        <!-- Default configurable card layout -->
        <template v-else>
          <!-- Checkbox (if enabled) -->
          <div v-if="selectable" class="themed-grid__checkbox" @click.stop>
          <themed-checkbox
            :value="isSelected(item)"
            @input="handleCheckboxChange(item, $event)"
            :aria-label="`${isSelected(item) ? 'Deselect' : 'Select'} ${getItemTitle(item)}`"
          />
        </div>

        <!-- Badge (top-left) -->
        <div
          v-for="badge in getItemBadges(item, 'topLeft')"
          :key="badge.field"
          :class="['themed-grid__badge themed-grid__badge--top-left', badge.class]"
        >
          {{ badge.value }}
        </div>

        <!-- Image Section (only if image field is configured) -->
        <div v-if="imageFieldConfig" class="themed-grid__image-wrapper">
          <img
            :src="getImageUrl(item)"
            :alt="getItemTitle(item)"
            class="themed-grid__image"
            loading="lazy"
          />

          <!-- Badge (top-right) -->
          <span
            v-for="badge in getItemBadges(item, 'topRight')"
            :key="badge.field"
            :class="['themed-grid__badge themed-grid__badge--top-right', badge.class]"
            role="status"
          >
            {{ badge.value }}
          </span>
        </div>

        <!-- Badge (top-right) without image -->
        <div
          v-else
          v-for="badge in getItemBadges(item, 'topRight')"
          :key="badge.field"
          :class="['themed-grid__badge themed-grid__badge--top-right-no-image', badge.class]"
          role="status"
        >
          {{ badge.value }}
        </div>

        <!-- Content Section -->
        <div class="themed-grid__content">
          <!-- Header -->
          <div class="themed-grid__header">
            <div class="themed-grid__title" :title="getItemTitle(item)">
              {{ getItemTitle(item) }}
            </div>
            <div v-if="getItemSubtitle(item)" class="themed-grid__subtitle">
              {{ getItemSubtitle(item) }}
            </div>
          </div>

          <!-- Fields/Metrics -->
          <div class="themed-grid__fields">
            <template v-for="(field, fieldIndex) in displayFields">
              <!-- Two-column row -->
              <div
                v-if="field.columns === 2"
                :key="field.field || `row-${fieldIndex}`"
                :class="['themed-grid__field-row', field.class]"
              >
                <div
                  v-for="(subField, subIndex) in field.fields"
                  :key="`${field.field || fieldIndex}-${subField.field || subIndex}`"
                  class="themed-grid__field"
                >
                  <!-- Label first (when labelFirst is true and showLabel is not false and labelInValue is false) -->
                  <div
                    v-if="shouldShowLabel(subField) && subField.labelFirst && !subField.labelInValue"
                    class="themed-grid__field-label"
                  >
                    {{ subField.label }}
                  </div>

                  <!-- Badge wrapper for field -->
                  <template v-if="subField.badge">
                    <div class="themed-grid__field-badge-wrapper">
                      <div
                        :class="['themed-grid__field-badge', getFieldClass(item, subField)]"
                      >
                        <!-- Label inside badge (when labelInValue is true) -->
                        <template v-if="subField.labelInValue && shouldShowLabel(subField)">
                          {{ formatFieldValue(item, subField) }}
                          <span class="themed-grid__field-badge-label">{{ subField.label }}</span>
                        </template>
                        <template v-else>
                          {{ formatFieldValue(item, subField) }}
                        </template>
                      </div>

                      <!-- Info icon outside badge -->
                      <info-icon
                        v-if="subField.infoIcon"
                        :key="`info-${item.id || index}-${subField.field}`"
                        :identifier="subField.infoIcon"
                        class="themed-grid__field-info-icon"
                      />
                    </div>
                  </template>
                  <!-- Regular field value -->
                  <div
                    v-else
                    :class="['themed-grid__field-value', getFieldClass(item, subField)]"
                  >
                    <!-- Label inside value (when labelInValue is true) -->
                    <template v-if="subField.labelInValue && shouldShowLabel(subField)">
                      {{ formatFieldValue(item, subField) }}
                      <span class="themed-grid__field-value-label">{{ subField.label }}</span>
                    </template>
                    <template v-else>
                      {{ formatFieldValue(item, subField) }}
                    </template>
                  </div>

                  <!-- Label after (default position when labelFirst is false/undefined and labelInValue is false) -->
                  <div
                    v-if="shouldShowLabel(subField) && !subField.labelFirst && !subField.labelInValue"
                    class="themed-grid__field-label"
                  >
                    {{ subField.label }}
                  </div>
                </div>
              </div>

              <!-- Single column field -->
              <div
                v-else
                :key="field.field"
                :class="['themed-grid__field', field.class]"
              >
                <!-- Label first (when labelFirst is true and showLabel is not false and labelInValue is false) -->
                <div
                  v-if="shouldShowLabel(field) && field.labelFirst && !field.labelInValue"
                  class="themed-grid__field-label"
                >
                  {{ field.label }}
                </div>

                <!-- Badge wrapper for field -->
                <template v-if="field.badge">
                  <div class="themed-grid__field-badge-wrapper">
                    <div
                      :class="['themed-grid__field-badge', getFieldClass(item, field)]"
                    >
                      <!-- Label inside badge (when labelInValue is true) -->
                      <template v-if="field.labelInValue && shouldShowLabel(field)">
                        {{ formatFieldValue(item, field) }}
                        <span class="themed-grid__field-badge-label">{{ field.label }}</span>
                      </template>
                      <template v-else>
                        {{ formatFieldValue(item, field) }}
                      </template>
                    </div>

                    <!-- Info icon outside badge -->
                    <info-icon
                      v-if="field.infoIcon"
                      :key="`info-${item.id || index}-${field.field}`"
                      :identifier="field.infoIcon"
                      class="themed-grid__field-info-icon"
                    />
                  </div>
                </template>
                <!-- Regular field value -->
                <div
                  v-else
                  :class="['themed-grid__field-value', getFieldClass(item, field)]"
                >
                  <!-- Label inside value (when labelInValue is true) -->
                  <template v-if="field.labelInValue && shouldShowLabel(field)">
                    {{ formatFieldValue(item, field) }}
                    <span class="themed-grid__field-value-label">{{ field.label }}</span>
                  </template>
                  <template v-else>
                    {{ formatFieldValue(item, field) }}
                  </template>
                </div>

                <!-- Label after (default position when labelFirst is false/undefined and labelInValue is false) -->
                <div
                  v-if="shouldShowLabel(field) && !field.labelFirst && !field.labelInValue"
                  class="themed-grid__field-label"
                >
                  {{ field.label }}
                </div>
              </div>
            </template>
          </div>

          <!-- Footer/Actions -->
          <div v-if="hasFooter" class="themed-grid__footer">
            <slot name="actions" :item="item">
              <div
                v-for="action in getItemActions(item)"
                :key="action.name"
                @click.stop="handleActionClick(action, item)"
                style="cursor: pointer;"
              >
                <themed-button
                  :variant="action.variant || 'default'"
                  :size="action.size || 'sm'"
                  :disabled="isActionDisabled(action, item)"
                  class="themed-grid__action-button"
                  style="pointer-events: none;"
                >
                  <theme-icon
                    v-if="action.icon"
                    :icon="action.icon"
                    type="icons"
                    classes="w-4 h-4"
                  />
                  <span>{{ action.label }}</span>
                </themed-button>
              </div>
            </slot>
          </div>
        </div>
        </template>
      </div>
    </div>

    <!-- Empty State -->
    <table-empty
      v-if="data.length === 0"
      :loading="loading"
      :is-empty="!loading"
      :icon="emptyIcon"
      icon-type="icons"
      :message="emptyMessage"
      :loading-message="loadingMessage"
      custom-class="themed-grid__empty card"
    />

    <!-- Pagination -->
    <themed-pagination
      v-if="showPagination"
      :current-page="currentPageNumber"
      :total-pages="totalPages"
      :total-items="data.length"
      :per-page="currentPerPage"
      @first="goToFirstPage"
      @previous="previousPage"
      @next="nextPage"
      @last="goToLastPage"
    />
  </div>
</template>

<script>
import ThemedButton from './ThemedButton.vue';
import ThemedCheckbox from './ThemedCheckbox.vue';
import ThemeIcon from './ThemeIcon.vue';
import ThemedPagination from './ThemedPagination.vue';
import TableEmpty from './table/TableEmpty.vue';
import InfoIcon from './InfoIcon.vue';
import { formatNumber, formatCurrencyUnits, formatShorthandCurrency, formatDate, formatPercentage } from '../../../utils/formatters';

export default {
  name: 'ThemedGrid',

  components: {
    ThemedButton,
    ThemedCheckbox,
    ThemeIcon,
    ThemedPagination,
    TableEmpty,
    InfoIcon
  },

  props: {
    // Data
    data: {
      type: Array,
      required: true,
      default: () => []
    },

    // Field configuration
    fields: {
      type: Array,
      required: true,
      default: () => []
      // Example:
      // [
      //   { field: 'image_url', type: 'image', defaultImage: '/img/placeholder.jpg' },
      //   { field: 'name', type: 'title' },
      //   { field: 'location', type: 'subtitle' },
      //   {
      //     field: 'is_imported',
      //     type: 'badge',
      //     position: 'topLeft',
      //     transform: (value) => value ? 'IMPORTED' : null,
      //     class: 'badge--primary'
      //   },
      //   {
      //     field: 'status',
      //     type: 'badge',
      //     position: 'topRight',
      //     classFunction: (item) => item.status === 'available' ? 'badge--green' : 'badge--yellow'
      //   },
      //   {
      //     columns: 2,
      //     fields: [
      //       { field: 'city', label: 'City', type: 'text' },
      //       { field: 'state', label: 'State', type: 'text' }
      //     ]
      //   },
      //   { field: 'price', label: 'Price', type: 'currency', precision: 2 }
      // ]
    },

    // Column visibility
    visibleFields: {
      type: Array,
      default: null
      // Array of field names that should be visible
      // If not provided, all fields will be visible
    },

    // Deprecated props (kept for backward compatibility)
    titleField: {
      type: String,
      default: null
    },

    subtitleField: {
      type: String,
      default: null
    },

    imageField: {
      type: String,
      default: null
    },

    defaultImage: {
      type: String,
      default: '/img/placeholder-building.jpg'
    },

    // Actions configuration
    actions: {
      type: Array,
      default: () => []
      // Example:
      // [
      //   { name: 'view', label: 'View', icon: 'eye', variant: 'default' },
      //   { name: 'edit', label: 'Edit', icon: 'pencil', variant: 'outline' },
      //   { name: 'delete', label: 'Delete', icon: 'trash', variant: 'destructive' }
      // ]
    },

    // Selection
    selectable: {
      type: Boolean,
      default: false
    },

    selectedItems: {
      type: Array,
      default: () => []
    },

    // Grid layout
    columns: {
      type: Number,
      default: 4,
      validator: (value) => [1, 2, 3, 4, 5, 6].includes(value)
    },

    // Pagination
    pagination: {
      type: [Boolean, Object],
      default: true
      // Pass true to enable pagination with defaults (12 per page)
      // Or pass object: { current, total, perPage, totalItems } for external control
      // Pass false to disable pagination
    },

    perPage: {
      type: Number,
      default: 12
      // Deprecated: Use pagination object instead
    },

    currentPage: {
      type: Number,
      default: 1
      // Deprecated: Use pagination object instead
    },

    // Item key
    itemKey: {
      type: String,
      default: 'id'
    },

    // Empty state
    emptyMessage: {
      type: String,
      default: 'No items found'
    },

    emptyIcon: {
      type: String,
      default: 'home-icon'
    },

    // Loading state
    loading: {
      type: Boolean,
      default: false
    },

    loadingMessage: {
      type: String,
      default: 'Loading...'
    },

    // Click behavior
    clickable: {
      type: Boolean,
      default: true
    },

    // Custom class
    customClass: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      internalCurrentPage: this.currentPage
    };
  },

  computed: {
    gridClass() {
      return `themed-grid__container--cols-${this.columns}`;
    },

    imageFieldConfig() {
      // Check for image field in fields array
      const imageFieldConfig = this.fields.find(f => f.type === 'image');
      if (imageFieldConfig) {
        return imageFieldConfig;
      }

      // Fallback to deprecated imageField prop
      if (this.imageField) {
        return {
          field: this.imageField,
          type: 'image',
          defaultImage: this.defaultImage
        };
      }

      return null;
    },

    titleFieldConfig() {
      // Check for title field in fields array
      const titleFieldConfig = this.fields.find(f => f.type === 'title');
      if (titleFieldConfig) {
        return titleFieldConfig;
      }

      // Fallback to deprecated titleField prop
      if (this.titleField) {
        return {
          field: this.titleField,
          type: 'title'
        };
      }

      return null;
    },

    subtitleFieldConfig() {
      // Check for subtitle field in fields array
      const subtitleFieldConfig = this.fields.find(f => f.type === 'subtitle');
      if (subtitleFieldConfig) {
        return subtitleFieldConfig;
      }

      // Fallback to deprecated subtitleField prop
      if (this.subtitleField) {
        return {
          field: this.subtitleField,
          type: 'subtitle'
        };
      }

      return null;
    },

    badgeFields() {
      return this.fields.filter(field => field.type === 'badge');
    },

    displayFields() {
      // Filter out special field types
      const allDisplayFields = this.fields.filter(field =>
        field.type !== 'badge' &&
        field.type !== 'image' &&
        field.type !== 'title' &&
        field.type !== 'subtitle' &&
        field.visible !== false
      );

      // If visibleFields prop is not provided, return all fields
      if (!this.visibleFields || this.visibleFields.length === 0) {
        return allDisplayFields;
      }

      // Filter based on visibleFields array
      return allDisplayFields.filter(field => {
        // Handle two-column rows
        if (field.columns === 2 && field.fields) {
          // Check if any subfield is in visibleFields
          return field.fields.some(subField => this.visibleFields.includes(subField.field));
        }

        // Handle single fields
        return this.visibleFields.includes(field.field);
      }).map(field => {
        // For two-column rows, filter subfields
        if (field.columns === 2 && field.fields) {
          return {
            ...field,
            fields: field.fields.filter(subField => this.visibleFields.includes(subField.field))
          };
        }

        return field;
      });
    },

    hasActions() {
      return this.actions && this.actions.length > 0;
    },

    hasFooter() {
      return this.hasActions || this.$scopedSlots.actions;
    },

    hasPagination() {
      return !!this.pagination;
    },

    currentPerPage() {
      // Use pagination object if provided
      if (this.pagination && typeof this.pagination === 'object' && this.pagination.perPage) {
        return this.pagination.perPage;
      }

      // Fallback to perPage prop
      return this.perPage;
    },

    currentPageNumber() {
      // Use pagination object if provided
      if (this.pagination && typeof this.pagination === 'object' && this.pagination.current) {
        return this.pagination.current;
      }

      // Fallback to internal state or currentPage prop
      return this.internalCurrentPage;
    },

    paginatedData() {
      if (!this.hasPagination) {
        return this.data;
      }

      const start = (this.currentPageNumber - 1) * this.currentPerPage;
      const end = start + this.currentPerPage;
      return this.data.slice(start, end);
    },

    totalPages() {
      if (!this.hasPagination) {
        return 1;
      }

      // Use pagination object if provided
      if (this.pagination && typeof this.pagination === 'object' && this.pagination.total) {
        return this.pagination.total;
      }

      // Calculate from data
      return Math.ceil(this.data.length / this.currentPerPage);
    },

    showPagination() {
      return this.hasPagination && this.totalPages > 1;
    }
  },

  watch: {
    currentPage(newValue) {
      this.internalCurrentPage = newValue;
    },

    data() {
      // Reset to first page when data changes
      this.internalCurrentPage = 1;
    }
  },

  methods: {
    getItemKey(item, index) {
      return item[this.itemKey] || index;
    },

    shouldShowLabel(field) {
      // If showLabel is explicitly set to false, don't show label
      if (field.showLabel === false) {
        return false;
      }

      // If label exists and showLabel is not false, show it
      return !!field.label;
    },

    getItemTitle(item) {
      if (!this.titleFieldConfig) return 'Unknown';

      const value = this.getNestedValue(item, this.titleFieldConfig.field);

      // Apply transform if provided
      if (this.titleFieldConfig.transform && typeof this.titleFieldConfig.transform === 'function') {
        return this.titleFieldConfig.transform(value, item);
      }

      return value || 'Unknown';
    },

    getItemSubtitle(item) {
      if (!this.subtitleFieldConfig) return null;

      const value = this.getNestedValue(item, this.subtitleFieldConfig.field);

      // Apply transform if provided
      if (this.subtitleFieldConfig.transform && typeof this.subtitleFieldConfig.transform === 'function') {
        return this.subtitleFieldConfig.transform(value, item);
      }

      return value;
    },

    getImageUrl(item) {
      if (!this.imageFieldConfig) {
        return this.defaultImage;
      }

      const imageValue = this.getNestedValue(item, this.imageFieldConfig.field);

      // Handle object with view_url property
      if (imageValue && typeof imageValue === 'object' && imageValue.view_url) {
        return imageValue.view_url;
      }

      // Handle direct URL string
      if (typeof imageValue === 'string') {
        return imageValue;
      }

      // Use field-specific default or global default
      return this.imageFieldConfig.defaultImage || this.defaultImage;
    },

    getItemBadges(item, position) {
      return this.badgeFields
        .filter(field => field.position === position)
        .map(field => {
          let value = this.getNestedValue(item, field.field);

          // Apply transform function
          if (field.transform && typeof field.transform === 'function') {
            value = field.transform(value, item);
          }

          // Skip if no value
          if (!value) return null;

          // Get badge class
          let badgeClass = '';
          if (field.classField) {
            badgeClass = this.getNestedValue(item, field.classField) || '';
          } else if (field.class) {
            badgeClass = field.class;
          } else if (field.classFunction && typeof field.classFunction === 'function') {
            badgeClass = field.classFunction(item);
          }

          return {
            field: field.field,
            value,
            class: badgeClass
          };
        })
        .filter(badge => badge !== null);
    },

    formatFieldValue(item, field) {
      let value = this.getNestedValue(item, field.field);

      // Apply transform function if provided
      if (field.transform && typeof field.transform === 'function') {
        value = field.transform(value, item);
      }

      // Handle null/undefined
      if (value === null || value === undefined || value === '') {
        return '-';
      }

      // Format based on type
      switch (field.type) {
        case 'currency':
          // Whole units, so the field needs a transform if the attribute is cents.
          return formatCurrencyUnits(value, field.precision || 0);

        case 'shorthand-currency':
          return formatShorthandCurrency(value);

        case 'number':
          return formatNumber(value);

        case 'percentage':
          return formatPercentage(value, field.precision || 0);

        case 'date':
          return formatDate(value);

        case 'badge':
          return value;

        default:
          return value;
      }
    },

    getFieldClass(item, field) {
      const classes = [];

      // Add type-based class
      if (field.type) {
        classes.push(`themed-grid__field-value--${field.type}`);
      }

      // Add badge-specific class if this field is a badge
      if (field.badge) {
        // Get badge class from various sources
        if (field.badgeClass && typeof field.badgeClass === 'function') {
          const cellValue = this.getNestedValue(item, field.field);
          const badgeClass = field.badgeClass(cellValue, item);
          if (badgeClass) {
            classes.push(badgeClass);
          }
        } else if (field.badgeClass && typeof field.badgeClass === 'string') {
          // Handle badgeClass as a string
          classes.push(field.badgeClass);
        } else if (field.classFunction && typeof field.classFunction === 'function') {
          const dynamicClass = field.classFunction(item);
          if (dynamicClass) {
            classes.push(dynamicClass);
          }
        } else if (field.class) {
          classes.push(field.class);
        }
      } else {
        // Add custom class from field config (for non-badge fields)
        if (field.class) {
          classes.push(field.class);
        }

        // Add dynamic class from classFunction (for non-badge fields)
        if (field.classFunction && typeof field.classFunction === 'function') {
          const dynamicClass = field.classFunction(item);
          if (dynamicClass) {
            classes.push(dynamicClass);
          }
        }
      }

      return classes.join(' ');
    },

    getItemActions(item) {
      return this.actions.filter(action => {
        // Check if action should be visible for this item
        if (action.visible && typeof action.visible === 'function') {
          return action.visible(item);
        }
        return true;
      }).map(action => ({
        ...action,
        disabled: action.disabled && typeof action.disabled === 'function'
          ? action.disabled(item)
          : action.disabled
      }));
    },

    isActionDisabled(action, item) {
      if (action.disabled && typeof action.disabled === 'function') {
        return action.disabled(item);
      }
      return action.disabled || false;
    },

    getActionTooltip(action, item) {
      // If action has a tooltip function, call it
      if (action.tooltip && typeof action.tooltip === 'function') {
        return action.tooltip(item);
      }

      // If action has a static tooltip string, return it
      if (action.tooltip && typeof action.tooltip === 'string') {
        return action.tooltip;
      }

      // No tooltip
      return null;
    },

    getNestedValue(obj, path) {
      if (!path) return null;

      return path.split('.').reduce((current, key) => {
        return current?.[key];
      }, obj);
    },

    isSelected(item) {
      const itemId = item[this.itemKey];
      return this.selectedItems.some(selected => selected[this.itemKey] === itemId);
    },

    handleCardClick(item) {
      if (this.clickable && !this.selectable) {
        this.$emit('card-click', item);
      }
    },

    handleCheckboxChange(item, selected) {
      this.$emit('selection-change', { item, selected });
    },

    handleAction(actionName, item) {
      this.$emit('action', { action: actionName, item });
    },

    handleActionClick(action, item) {
      // Check if button is disabled
      const isDisabled = this.isActionDisabled(action, item);

      if (isDisabled) {
        // Get the tooltip message and show it as a toast
        const message = this.getActionTooltip(action, item);
        if (message) {
          // Emit event for parent to handle toast
          this.$emit('disabled-action-click', { action: action.name, item, message });
        }
      } else {
        // Button is enabled, proceed with normal action
        this.handleAction(action.name, item);
      }
    },

    goToFirstPage() {
      this.internalCurrentPage = 1;
      this.$emit('update:currentPage', 1);
      this.$emit('page-change', { page: 1 });
    },

    previousPage() {
      if (this.currentPageNumber > 1) {
        this.internalCurrentPage--;
        this.$emit('update:currentPage', this.internalCurrentPage);
        this.$emit('page-change', { page: this.internalCurrentPage });
      }
    },

    nextPage() {
      if (this.currentPageNumber < this.totalPages) {
        this.internalCurrentPage++;
        this.$emit('update:currentPage', this.internalCurrentPage);
        this.$emit('page-change', { page: this.internalCurrentPage });
      }
    },

    goToLastPage() {
      this.internalCurrentPage = this.totalPages;
      this.$emit('update:currentPage', this.totalPages);
      this.$emit('page-change', { page: this.totalPages });
    }
  }
};
</script>

<style lang="less">
@import '../../../../less/theme/v1/components/themed-grid.less';
</style>
